import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './api';

export interface SessionInfo {
  user: { id: string; email: string; name: string; locale: string; uiMode: 'beginner' | 'advanced' };
  session: {
    id: string;
    activeOrganizationId: string | null;
    mfaVerified: boolean;
    mfaVerifiedAt: string | null;
    expiresAt: string;
  };
  mfaEnrolled: boolean;
  memberships: Array<{
    organizationId: string;
    organizationName: string;
    roleKey: string;
    /** Panelin görünen adı (F12-12); null = varsayılan. */
    systemName: string | null;
  }>;
}

export interface MeInfo {
  user: {
    id: string;
    email: string;
    name: string;
    phone: string | null;
    locale: string;
    uiMode: 'beginner' | 'advanced';
  };
  organizationId: string;
  roleKey: string | null;
  permissions: string[];
  storeScope: string[];
  /** Bayi veri kapsamı (F7-08). */
  dealerScope?: 'all' | 'assigned';
  memberships: SessionInfo['memberships'];
}

/** Oturum yoksa null döner (401 hata sayılmaz). */
export function useSession() {
  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        return await api.get<SessionInfo>('/v1/auth/session');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 30_000,
    retry: false,
  });
}

export function useMe(enabled = true) {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeInfo>('/v1/admin/me'),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function usePermissions() {
  const me = useMe();
  const perms = new Set(me.data?.permissions ?? []);
  return {
    can: (p: string) => perms.has(p),
    roleKey: me.data?.roleKey ?? null,
    loaded: me.isSuccess,
    /** Bayi kapsamı "yalnızca atanan bayiler" (F7-08): tüm bayileri ilgilendiren işlemler gizlenir. */
    dealerScoped: me.data?.dealerScope === 'assigned',
  };
}

export function useInvalidateSession() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['session'] }),
      qc.invalidateQueries({ queryKey: ['me'] }),
    ]);
}
